import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { FaceFrownIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { useCookies } from 'react-cookie';
import { LocationContext } from '../../../../../shared/Context';
import useMapboxSearch from './useMapboxSearch/useMapboxSearch';
import { getLatLngFromMapboxFeature } from './useMapboxSearch/utils';

function SearchLocationInput({ setOpen }) {
  const { setLocationData } = useContext(LocationContext);
  const [_cookies, setCookie] = useCookies(['rcGeo']);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = useMapboxSearch({
    requestOptions: {
      /* Define search scope here */
    },
    debounce: 300,
  });

  function handleInput(v) {
    setValue(v);
  }

  function handleSelect(item) {
    if (!item) {
      return;
    }

    try {
      // Mapbox feature already contains coordinates, so we can extract them directly
      const locData = getLatLngFromMapboxFeature(item);

      setValue(item.description || item.formatted_address || '', false);
      clearSuggestions();

      setCookie('rcGeo', locData, {
        path: '/',
        maxAge: 3600 * 24 * 365,
      });
      setLocationData(locData);
      setValue('');

      setOpen(false);
    } catch (error) {
      console.error(`😱 Error: ${error}`);
    }
  }

  return (
    <Combobox onChange={(item) => handleSelect(item)}>
      <div className="relative mt-4">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>

        <ComboboxInput
          className="block w-full rounded-md border-0 py-1.5 pl-10 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-rcprimary-400 focus:ring-inset sm:text-sm sm:leading-6"
          placeholder="Search..."
          onChange={(event) => handleInput(event.target.value)}
          aria-disabled={!ready}
        />
      </div>

      {value === '' && (
        <div className="h-96 border-white/20 border-t px-6 py-14 text-center text-sm sm:px-14">
          <p className="mt-4 font-semibold text-rcgray-700">&nbsp;</p>
        </div>
      )}

      {status === 'OK' && (
        <ComboboxOptions
          static
          className="mt-1 h-96 max-h-96 scroll-py-3 divide-y divide-black/10 overflow-y-auto rounded-lg border border-black/20"
        >
          {data.map((suggestion) => {
            const {
              place_id,
              structured_formatting,
              description,
              formatted_address,
            } = suggestion;

            const main_text =
              structured_formatting?.main_text ||
              description?.split(',')[0] ||
              formatted_address?.split(',')[0] ||
              '';
            const secondary_text =
              structured_formatting?.secondary_text ||
              description?.split(',').slice(1).join(',').trim() ||
              formatted_address?.split(',').slice(1).join(',').trim() ||
              '';

            return (
              <ComboboxOption
                key={place_id}
                value={suggestion}
                className="flex cursor-pointer select-none py-3 data-active:bg-black/10 data-active:text-black"
              >
                <div className="ml-4 flex-auto">
                  {main_text && (
                    <p className="font-medium text-black text-sm data-active:text-rcprimary-400">
                      {main_text}
                    </p>
                  )}
                  {secondary_text && (
                    <p className="text-black text-sm data-active:text-rcprimary-400">
                      {secondary_text}
                    </p>
                  )}
                  {!(main_text || secondary_text) && (
                    <p className="font-medium text-black text-sm data-active:text-rcprimary-400">
                      {description || formatted_address || 'Location'}
                    </p>
                  )}
                </div>
              </ComboboxOption>
            );
          })}
        </ComboboxOptions>
      )}

      {value !== '' && data.length === 0 && (
        <div className="h-96 border-white/20 border-t px-6 py-14 text-center text-sm sm:px-14">
          <FaceFrownIcon
            className="mx-auto h-6 w-6 text-black/60"
            aria-hidden="true"
          />
          <p className="mt-4 font-semibold text-black">No results found</p>
          <p className="mt-2 text-black-70">
            We couldn&apos;t locate you with that search. Please try again.
          </p>
        </div>
      )}
    </Combobox>
  );
}

SearchLocationInput.propTypes = {
  setOpen: PropTypes.func.isRequired,
};

export default SearchLocationInput;
